import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { EnvelopeSimple, ArrowLeft, CheckCircle } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiErrorDetail(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).filter(Boolean).join(' ');
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(formatApiErrorDetail(detail) || 'Could not send the reset link. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50 via-white to-blue-50/30 px-4 relative overflow-hidden">
      <div className="absolute top-1/4 -left-32 w-96 h-96 bg-amber-100/40 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-1/4 -right-32 w-96 h-96 bg-blue-100/30 rounded-full blur-3xl pointer-events-none" />
      <div className="w-full max-w-md relative z-10">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-white border border-amber-200 shadow-sm mb-4">
            <EnvelopeSimple size={26} className="text-blue-700" weight="duotone" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight" style={{ fontFamily: "'Libre Baskerville', serif" }}>
            {sent ? 'Check your email' : 'Reset your password'}
          </h1>
          <p className="text-gray-600 mt-2 text-sm">
            {sent ? "We've sent a reset link if your email is registered with us." : "Enter your account email and we'll send a secure reset link."}
          </p>
        </div>
        <div className="bg-white/80 backdrop-blur border border-gray-100 rounded-2xl p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
          {sent ? (
            <div className="space-y-5" data-testid="forgot-password-sent">
              <div className="flex items-start gap-3 p-4 bg-emerald-50 border border-emerald-200 rounded-md">
                <CheckCircle size={20} className="text-emerald-700 flex-shrink-0 mt-0.5" weight="fill" />
                <div className="text-sm text-emerald-900">
                  <p className="font-semibold mb-1">Link sent to {email}</p>
                  <p className="text-emerald-800">It&apos;s valid for 60 minutes and can only be used once. Check your spam folder if you don&apos;t see it within a few minutes.</p>
                </div>
              </div>
              <Button onClick={() => { setSent(false); setEmail(''); }} variant="outline" className="w-full h-11 border-blue-200 text-blue-700 hover:bg-blue-50" data-testid="forgot-password-resend-btn">
                Send to a different email
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="forgot-password-form">
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700" data-testid="forgot-password-error">{error}</div>
              )}
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <Input id="email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@studyalliance.ca" required data-testid="forgot-password-email-input" className="h-11" />
              </div>
              <Button type="submit" disabled={loading || !email.trim()} className="w-full bg-blue-700 hover:bg-blue-800 text-white h-11 font-semibold" data-testid="forgot-password-submit-btn">
                {loading ? 'Sending…' : 'Send reset link'}
              </Button>
            </form>
          )}
        </div>
        <p className="text-center text-xs text-gray-400 mt-6">
          <Link to="/login" className="inline-flex items-center gap-1 text-gray-600 hover:text-blue-700" data-testid="forgot-password-back-to-login">
            <ArrowLeft size={12} /> Back to sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
